/**
 * Shared token metadata service.
 * Fetches token name, symbol, and logo via getAsset / getAssetsByOwner (Helius DAS or backend proxy).
 * All pages share the same in-memory caches; when cache is warm, no RPC is called:
 * - Wallet token list: getWalletTokensFromCache → skip getParsedTokenAccountsByOwner.
 * - Mint info (revoke/decimals): getMintInfoCached → skip getMint/getAccountInfo.
 * - Metadata bulk: getAssetsByOwnerMetadataMap cache → skip getAssetsByOwner.
 * - Per-mint metadata: localStorage cache; enrichment never calls getAsset (getAssetsByOwner + fallback only).
 * All caches are persisted in localStorage (survives refresh/tab close).
 */

import { PublicKey } from '@solana/web3.js';
import { getMint, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { rewriteFlakyIpfsGatewayUrl } from '@/lib/ipfs/rewriteFlakyIpfsGatewayUrl';

const METADATA_RATE_LIMIT_MS = 2000;
const MAX_METADATA_CACHE_AGE_MS = 10 * 60 * 1000; // 10 minutes
const MAX_CONCURRENT_REQUESTS = 2;

// Cache TTLs
const WALLET_TOKENS_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
const MINT_INFO_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes
const ASSETS_BY_OWNER_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes

const STORAGE_PREFIX = 'autobot_tokens_';

function pinataLogo(url) {
  if (!url) return null;
  return rewriteFlakyIpfsGatewayUrl(url) || url;
}

function isStorageAvailable() {
  try {
    return typeof window !== 'undefined' && window.localStorage != null;
  } catch {
    return false;
  }
}

function storageGet(key) {
  if (!isStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  if (!isStorageAvailable()) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // quota exceeded or disabled; ignore
  }
}

/** In-flight dedupe: one RPC per wallet / per wallet+rpcUrl so concurrent callers share the same request */
const pendingWalletFetch = new Map(); // walletAddress -> Promise<rawTokenList>
const pendingAssetsByOwner = new Map(); // cacheKey -> Promise<Map>

const metadataFetchQueue = [];
const metadataPendingByMint = new Map(); // mintAddress -> Promise
let lastMetadataFetch = 0;
let activeRequests = 0;
let isProcessingQueue = false;

function getFallback(mintAddress) {
  return {
    name: `Token ${mintAddress.slice(0, 8)}`,
    symbol: mintAddress.slice(0, 4).toUpperCase(),
    logo: null,
  };
}

/**
 * Get cached raw wallet token list for a given address, if it is still fresh (localStorage).
 *
 * @param {string} walletAddress
 * @returns {Array| null}
 */
export function getWalletTokensFromCache(walletAddress) {
  if (!walletAddress) return null;
  const entry = storageGet('wallet_' + walletAddress);
  if (!entry || !entry.tokens) return null;
  if (Date.now() - (entry.timestamp || 0) > WALLET_TOKENS_MAX_AGE_MS) {
    try {
      if (isStorageAvailable()) localStorage.removeItem(STORAGE_PREFIX + 'wallet_' + walletAddress);
    } catch {}
    return null;
  }
  return entry.tokens;
}

/**
 * Store raw wallet token list in localStorage.
 *
 * @param {string} walletAddress
 * @param {Array} tokens
 */
export function setWalletTokensCache(walletAddress, tokens) {
  if (!walletAddress || !Array.isArray(tokens)) return;
  storageSet('wallet_' + walletAddress, {
    tokens,
    timestamp: Date.now(),
  });
}

/**
 * Get mint account info (mintAuthority, freezeAuthority, decimals) with shared 5-min cache.
 * Single place for getMint/getAccountInfo — no duplicate RPC per mint across pages.
 *
 * @param {import('@solana/web3.js').Connection} connection
 * @param {string} mintAddress
 * @param {import('@solana/web3.js').PublicKey} [programId] - TOKEN_PROGRAM_ID or TOKEN_2022_PROGRAM_ID; if omitted, tries both
 * @returns {Promise<{ mintAuthority: PublicKey | null, freezeAuthority: PublicKey | null, decimals: number, programId: PublicKey }>}
 */
export async function getMintInfoCached(connection, mintAddress, programId) {
  const now = Date.now();
  const entry = storageGet('mint_' + mintAddress);
  if (entry && now - (entry.timestamp || 0) < MINT_INFO_CACHE_AGE_MS) {
    return {
      mintAuthority: entry.mintAuthority ? new PublicKey(entry.mintAuthority) : null,
      freezeAuthority: entry.freezeAuthority ? new PublicKey(entry.freezeAuthority) : null,
      decimals: entry.decimals ?? 9,
      programId: entry.programIdUsed ? new PublicKey(entry.programIdUsed) : TOKEN_PROGRAM_ID,
    };
  }

  const mintPubkey = new PublicKey(mintAddress);
  const programIds = programId ? [programId] : [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
  let mintInfo;
  let programIdUsed = null;
  for (const id of programIds) {
    try {
      mintInfo = await getMint(connection, mintPubkey, undefined, id);
      programIdUsed = id;
      break;
    } catch {
      continue;
    }
  }
  const toStore = {
    mintAuthority: null,
    freezeAuthority: null,
    decimals: 9,
    programIdUsed: TOKEN_PROGRAM_ID.toBase58(),
    timestamp: now,
  };
  if (mintInfo) {
    toStore.mintAuthority = mintInfo.mintAuthority ? mintInfo.mintAuthority.toBase58() : null;
    toStore.freezeAuthority = mintInfo.freezeAuthority ? mintInfo.freezeAuthority.toBase58() : null;
    toStore.decimals = mintInfo.decimals;
    toStore.programIdUsed = programIdUsed.toBase58();
  }
  storageSet('mint_' + mintAddress, toStore);
  if (!mintInfo) {
    return {
      mintAuthority: null,
      freezeAuthority: null,
      decimals: 9,
      programId: TOKEN_PROGRAM_ID,
    };
  }
  return {
    mintAuthority: mintInfo.mintAuthority,
    freezeAuthority: mintInfo.freezeAuthority,
    decimals: mintInfo.decimals,
    programId: programIdUsed,
  };
}

/**
 * Drop cached mint authority/decimals for a mint (e.g. after revoke on-chain).
 * Without this, getMintInfoCached can return stale authorities for up to 5 minutes.
 *
 * @param {string} mintAddress
 */
export function clearMintInfoCache(mintAddress) {
  if (!mintAddress || !isStorageAvailable()) return;
  try {
    localStorage.removeItem(STORAGE_PREFIX + 'mint_' + mintAddress);
  } catch {
    // ignore
  }
}

/**
 * Fetch raw wallet token list (SPL + Token-2022) from RPC. Used internally by getWalletTokensWithMetadata.
 * @param {import('@solana/web3.js').Connection} connection
 * @param {string} walletAddress
 * @returns {Promise<Array<{ mintAddress: string, name: string, symbol: string, logo: null, balance: number, decimals: number, programId: string, tokenAccount?: string }>>}
 */
async function fetchRawWalletTokens(connection, walletAddress) {
  const publicKey = new PublicKey(walletAddress);
  const [tokenAccounts, token2022Accounts] = await Promise.all([
    connection.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_PROGRAM_ID }),
    connection.getParsedTokenAccountsByOwner(publicKey, { programId: TOKEN_2022_PROGRAM_ID }),
  ]);
  const rawTokenList = [];
  const seen = new Set();

  const process = (value, programId) => {
    (value?.value ?? []).forEach((account) => {
      const parsedInfo = account?.account?.data?.parsed?.info;
      if (!parsedInfo) return;
      const mintAddress = parsedInfo.mint;
      const tokenAmount = parsedInfo.tokenAmount;
      if (!tokenAmount || tokenAmount.uiAmount === 0 || tokenAmount.uiAmount === null) return;
      if (seen.has(mintAddress)) return;
      seen.add(mintAddress);
      rawTokenList.push({
        mintAddress,
        name: `Token ${mintAddress.slice(0, 4)}...${mintAddress.slice(-4)}`,
        symbol: mintAddress.slice(0, 4).toUpperCase(),
        logo: null,
        balance: tokenAmount.uiAmount,
        balanceRaw: tokenAmount.amount ?? String(tokenAmount.uiAmount),
        decimals: tokenAmount.decimals,
        programId: programId === TOKEN_2022_PROGRAM_ID ? 'token-2022' : 'spl-token',
        tokenAccount: account.pubkey?.toString?.(),
      });
    });
  };

  process(tokenAccounts, TOKEN_PROGRAM_ID);
  process(token2022Accounts, TOKEN_2022_PROGRAM_ID);
  return rawTokenList;
}

/**
 * Single source of truth for "tokens held by wallet" with metadata.
 * Uses shared caches (wallet list + mint info + metadata). No duplicate getAccountInfo/getAsset per page.
 *
 * @param {import('@solana/web3.js').Connection} connection
 * @param {string} walletAddress
 * @param {string} rpcUrl - Helius RPC or backend URL for getAsset/getAssetsByOwner
 * @param {{ filterByMintAuthority?: import('@solana/web3.js').PublicKey, filterByFreezeAuthority?: import('@solana/web3.js').PublicKey }} [options]
 * @returns {Promise<Array<{ mintAddress: string, name: string, symbol: string, logo: string|null, balance: number, decimals: number, programId: string, tokenAccount?: string }>>}
 */
export async function getWalletTokensWithMetadata(connection, walletAddress, rpcUrl, options = {}) {
  if (!walletAddress || !connection) return [];

  // 1) Pre-check wallet cache — avoid any RPC when cache hit
  let rawTokenList = getWalletTokensFromCache(walletAddress);
  if (!rawTokenList) {
    // 2) In-flight dedupe: concurrent callers for same wallet share one getParsedTokenAccountsByOwner
    let pending = pendingWalletFetch.get(walletAddress);
    if (!pending) {
      pending = (async () => {
        try {
          const list = await fetchRawWalletTokens(connection, walletAddress);
          setWalletTokensCache(walletAddress, list);
          return list;
        } finally {
          pendingWalletFetch.delete(walletAddress);
        }
      })();
      pendingWalletFetch.set(walletAddress, pending);
    }
    rawTokenList = await pending;
  }

  const { filterByMintAuthority, filterByFreezeAuthority } = options;
  if (filterByMintAuthority || filterByFreezeAuthority) {
    const mintProgramMap = {};
    rawTokenList.forEach((t) => {
      if (!t.mintAddress) return;
      if (!mintProgramMap[t.mintAddress]) {
        mintProgramMap[t.mintAddress] =
          t.programId === 'token-2022' ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
      }
    });
    const eligibleMints = new Set();
    await Promise.all(
      Object.entries(mintProgramMap).map(async ([mintAddress, programId]) => {
        try {
          const mintInfo = await getMintInfoCached(connection, mintAddress, programId);
          if (filterByMintAuthority && mintInfo.mintAuthority?.equals(filterByMintAuthority)) {
            eligibleMints.add(mintAddress);
          }
          if (filterByFreezeAuthority && mintInfo.freezeAuthority?.equals(filterByFreezeAuthority)) {
            eligibleMints.add(mintAddress);
          }
        } catch {
          // skip
        }
      })
    );
    rawTokenList = rawTokenList.filter((t) => eligibleMints.has(t.mintAddress));
  }

  return enrichTokenListWithMetadata(rawTokenList, rpcUrl, { walletAddress });
}

/**
 * Single getAsset call. Used internally by the queue processor.
 * @param {string} mintAddress
 * @param {string} rpcUrl - Helius RPC or backend proxy URL that supports getAsset
 * @returns {Promise<{ name: string, symbol: string, logo: string|null }>}
 */
async function fetchTokenMetadataDirect(mintAddress, rpcUrl) {
  let logoUrl = null;

  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: '1',
        method: 'getAsset',
        params: { id: mintAddress, options: { showFungible: true } },
      }),
    });

    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 10000;
      await new Promise((r) => setTimeout(r, waitTime));
      const fallback = getFallback(mintAddress);
      storageSet('meta_' + mintAddress, { data: fallback, timestamp: Date.now() });
      return fallback;
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.result) {
      if (data.result.content?.links?.image) {
        logoUrl = pinataLogo(data.result.content.links.image);
      } else if (data.result.content?.files && Array.isArray(data.result.content.files)) {
        const imageFile = data.result.content.files.find((f) => f.mime?.startsWith('image/'));
        if (imageFile?.uri) logoUrl = pinataLogo(imageFile.uri);
      }

      if (data.result.content?.metadata) {
        const metadata = data.result.content.metadata;
        const result = {
          name: metadata.name || getFallback(mintAddress).name,
          symbol: metadata.symbol || getFallback(mintAddress).symbol,
          logo: logoUrl,
        };
        storageSet('meta_' + mintAddress, { data: result, timestamp: Date.now() });
        return result;
      }
    }
  } catch {
    // fallback below
  }

  const fallback = { ...getFallback(mintAddress), logo: logoUrl };
  storageSet('meta_' + mintAddress, { data: fallback, timestamp: Date.now() });
  return fallback;
}

async function processMetadataQueue(rpcUrl) {
  if (isProcessingQueue || metadataFetchQueue.length === 0) return;

  isProcessingQueue = true;

  while (metadataFetchQueue.length > 0) {
    while (activeRequests >= MAX_CONCURRENT_REQUESTS) {
      await new Promise((r) => setTimeout(r, 500));
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastMetadataFetch;
    if (timeSinceLastFetch < METADATA_RATE_LIMIT_MS) {
      await new Promise((r) => setTimeout(r, METADATA_RATE_LIMIT_MS - timeSinceLastFetch));
    }

    const { mintAddress, resolve, reject } = metadataFetchQueue.shift();

    const cached = storageGet('meta_' + mintAddress);
    if (cached && cached.data && now - (cached.timestamp || 0) < MAX_METADATA_CACHE_AGE_MS) {
      resolve(cached.data);
      continue;
    }

    activeRequests++;
    try {
      const result = await fetchTokenMetadataDirect(mintAddress, rpcUrl);
      resolve(result);
    } catch (err) {
      reject(err);
    } finally {
      activeRequests--;
      lastMetadataFetch = Date.now();
      metadataPendingByMint.delete(mintAddress);
    }
  }

  isProcessingQueue = false;
}

/**
 * Fetch token metadata (name, symbol, logo) for a single mint.
 * Uses shared cache and queue; rate-limited to avoid 429.
 *
 * @param {string} mintAddress
 * @param {string} rpcUrl - Helius RPC or backend URL that supports getAsset
 * @returns {Promise<{ name: string, symbol: string, logo: string|null }>}
 */
export function fetchTokenMetadata(mintAddress, rpcUrl) {
  const cached = storageGet('meta_' + mintAddress);
  if (cached && cached.data && Date.now() - (cached.timestamp || 0) < MAX_METADATA_CACHE_AGE_MS) {
    const data = cached.data;
    return Promise.resolve(
      data?.logo ? { ...data, logo: pinataLogo(data.logo) } : data,
    );
  }

  const pending = metadataPendingByMint.get(mintAddress);
  if (pending) return pending;

  const promise = new Promise((resolve, reject) => {
    const wrappedResolve = (data) => {
      metadataPendingByMint.delete(mintAddress);
      resolve(data);
    };
    const wrappedReject = (err) => {
      metadataPendingByMint.delete(mintAddress);
      reject(err);
    };
    metadataFetchQueue.push({ mintAddress, resolve: wrappedResolve, reject: wrappedReject });
    processMetadataQueue(rpcUrl);
  });
  metadataPendingByMint.set(mintAddress, promise);
  return promise;
}

const FUNGIBLE_INTERFACES = ['FungibleToken', 'FungibleAsset'];

function assetsByOwnerCacheKey(walletAddress, rpcUrl) {
  return `${walletAddress}|${rpcUrl || ''}`;
}

/**
 * Bulk fetch wallet token metadata in one getAssetsByOwner call (Helius DAS API).
 * Uses shared cache: when cache is warm for this wallet+rpcUrl, no RPC call is made.
 *
 * @param {string} walletAddress
 * @param {string} rpcUrl - Helius RPC or backend URL that supports getAssetsByOwner
 * @returns {Promise<Map<string, { name: string, symbol: string, logo: string|null }>>} mintAddress -> metadata
 */
const ASSETS_STORAGE_KEY_PREFIX = 'assets_';

export async function getAssetsByOwnerMetadataMap(walletAddress, rpcUrl) {
  const cacheKey = assetsByOwnerCacheKey(walletAddress, rpcUrl);
  const storageKey = ASSETS_STORAGE_KEY_PREFIX + cacheKey;
  // 1) Pre-check localStorage cache — no RPC when cache hit
  const cached = storageGet(storageKey);
  if (cached && cached.metadataMap && Date.now() - (cached.timestamp || 0) < ASSETS_BY_OWNER_CACHE_AGE_MS) {
    return new Map(
      Object.entries(cached.metadataMap).map(([mint, meta]) => [
        mint,
        meta?.logo ? { ...meta, logo: pinataLogo(meta.logo) } : meta,
      ]),
    );
  }

  // 2) In-flight dedupe: concurrent callers for same wallet+rpcUrl share one getAssetsByOwner
  let pending = pendingAssetsByOwner.get(cacheKey);
  if (!pending) {
    pending = (async () => {
      try {
        const map = new Map();
        const res = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: '1',
            method: 'getAssetsByOwner',
            params: {
              ownerAddress: walletAddress,
              page: 1,
              limit: 1000,
              displayOptions: {
                showFungible: true,
                showZeroBalance: false,
              },
            },
          }),
        });
        if (!res.ok) return map;
        const data = await res.json();
        if (data.error || !data.result?.items) return map;

        const items = data.result.items.filter((a) => FUNGIBLE_INTERFACES.includes(a.interface));
        items.forEach((asset) => {
          const mintAddress = asset.id;
          if (!mintAddress) return;
          const logo =
            asset.content?.links?.image ??
            (asset.content?.files && Array.isArray(asset.content.files)
              ? asset.content.files.find((f) => f.mime?.startsWith('image/'))?.uri ?? null
              : null);
          map.set(mintAddress, {
            name:
              asset.content?.metadata?.name ??
              asset.content?.token_info?.symbol ??
              `Token ${mintAddress.slice(0, 8)}`,
            symbol:
              asset.content?.metadata?.symbol ??
              asset.content?.token_info?.symbol ??
              mintAddress.slice(0, 4).toUpperCase(),
            logo: pinataLogo(logo),
          });
        });
        storageSet(storageKey, {
          metadataMap: Object.fromEntries(map),
          timestamp: Date.now(),
        });
        return map;
      } catch {
        return new Map();
      } finally {
        pendingAssetsByOwner.delete(cacheKey);
      }
    })();
    pendingAssetsByOwner.set(cacheKey, pending);
  }
  return await pending;
}

/** Sync read of per-mint metadata cache from localStorage. Returns null if miss or stale. */
function getMetadataFromCacheSync(mintAddress) {
  const entry = storageGet('meta_' + mintAddress);
  if (!entry || !entry.data || Date.now() - (entry.timestamp || 0) > MAX_METADATA_CACHE_AGE_MS) return null;
  const data = entry.data;
  if (!data?.logo) return data;
  return { ...data, logo: pinataLogo(data.logo) };
}

/**
 * Enrich a list of raw tokens with name, symbol, and logo.
 * Uses only getAssetsByOwner (one bulk call, cached) + per-mint localStorage cache. No getAsset RPC.
 * Mints not in bulk or cache get fallback name/symbol (no extra RPC).
 */
export async function enrichTokenListWithMetadata(rawTokenList, rpcUrl, options = {}) {
  if (!rawTokenList || rawTokenList.length === 0) return [];

  const { walletAddress } =
    typeof options === 'object' && options !== null ? options : {};

  // 1) Bulk metadata from getAssetsByOwner (cached or single in-flight) — no getAsset
  let bulkMap = new Map();
  if (walletAddress && rpcUrl) {
    bulkMap = await getAssetsByOwnerMetadataMap(walletAddress, rpcUrl);
  }

  return rawTokenList.map((token) => {
    const bulkMeta = bulkMap.get(token.mintAddress);
    if (bulkMeta) {
      return { ...token, name: bulkMeta.name, symbol: bulkMeta.symbol, logo: bulkMeta.logo };
    }
    const cachedMeta = getMetadataFromCacheSync(token.mintAddress);
    if (cachedMeta) {
      return { ...token, name: cachedMeta.name, symbol: cachedMeta.symbol, logo: cachedMeta.logo };
    }
    const fallback = getFallback(token.mintAddress);
    return { ...token, name: fallback.name, symbol: fallback.symbol, logo: fallback.logo };
  });
}
